<template>
  <a
    v-if="href"
    class="relationship-tag-link"
    :class="{ 'relationship-tag-link--previous': previous }"
    :href="href"
    target="_blank"
    rel="noopener"
    :aria-label="`Open Tag ${name}${previous ? ' (previous link)' : ''} in a new tab`"
    @click.stop>
    <v-icon x-small class="relationship-tag-link__type">mdi-tag-outline</v-icon>
    <span class="relationship-tag-link__name">{{ name }}</span>
    <v-icon x-small class="relationship-tag-link__ext">mdi-open-in-new</v-icon>
  </a>
  <span
    v-else
    class="relationship-tag-link relationship-tag-link--static"
    :class="{ 'relationship-tag-link--previous': previous }">
    <v-icon x-small class="relationship-tag-link__type">mdi-tag-outline</v-icon>
    <span class="relationship-tag-link__name">{{ name }}</span>
  </span>
</template>

<script>
import { safeGuid } from '../../config/relationshipRefusalCopy';

// A Tag named in a run row. The run dialog lives in the central register workspace, which has no
// current Facility, so a navigable Tag always opens in a new tab (linkedTagOpensNewTab(null, id)
// in modules/registerTableLayout.js) with the same route RegisterTable.openLinkedRecord uses.
// A Tag with no Facility to route to (Facility Group scope) is shown as plain text.
export default {
  name: 'RelationshipTagLink',
  props: {
    name: { type: String, required: true },
    targetId: { type: String, default: null },
    facilityId: { type: String, default: null },
    previous: { type: Boolean, default: false },
  },
  computed: {
    href() {
      const tagId = safeGuid(this.targetId);
      const facilityId = safeGuid(this.facilityId);
      if (!tagId || !facilityId || !this.$router) return null;
      return this.$router.resolve({
        name: 'facility',
        params: { id: facilityId },
        query: { tagId, openPanel: 'true' },
      }).href;
    },
  },
};
</script>

<style lang="scss" scoped>
.relationship-tag-link {
  display: inline-flex;
  max-width: 100%;
  align-items: center;
  gap: var(--ds-space-xs);
  margin: -1px -4px;
  padding: 1px 4px;
  border-radius: var(--ds-radius-sm);
  color: var(--v-primary-base);
  font-weight: 600;
  text-decoration: none;
}
.relationship-tag-link .v-icon { color: inherit !important; }
.relationship-tag-link__name {
  min-width: 0;
  overflow: hidden;
  font-family: var(--ds-font-family-mono);
  font-size: 12.5px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.relationship-tag-link__ext { opacity: 0; transition: opacity 0.15s ease-out; }
a.relationship-tag-link:hover { background: var(--ds-surface-active); }
a.relationship-tag-link:hover .relationship-tag-link__name { text-decoration: underline; text-underline-offset: 3px; }
a.relationship-tag-link:hover .relationship-tag-link__ext,
a.relationship-tag-link:focus-visible .relationship-tag-link__ext { opacity: 1; }
a.relationship-tag-link:focus-visible {
  outline: var(--ds-focus-ring-width) solid var(--ds-focus-ring-color);
  outline-offset: var(--ds-focus-ring-offset);
}
.relationship-tag-link--static { color: var(--v-text-base); }
.relationship-tag-link--previous { color: var(--run-muted-ink, #5c6b66); font-weight: 500; }

@media (prefers-reduced-motion: reduce) {
  .relationship-tag-link__ext { transition: none; }
}
</style>
